import type { OutputOf } from "@adea/actions/spec";
import type { AdeaT, Formatter } from "@adea/i18n";
import type { getThread } from "@adea/product/core/core.specs";
import type { Attachment, Block, ThreadMessage, ThreadSubject, ToolStep } from "@adea/schemas";
import type { GlanceData, ThreadStep, ThreadStepKind, ThreadMessage as ViewMessage } from "@adea/ui";
import type { RunState } from "../../lib/run-fold.ts";

/**
 * Threads as the screens read them (`threads.get`, `threads.list` from `@adea/product/core/specs`), and the one step from a
 * stored thread (and the live run of its latest answer) to the messages `<ThreadView>` draws.
 */

export type Thread = OutputOf<typeof getThread>["thread"];
export type { Attachment, Block, ThreadMessage, ThreadSubject };

export type AskSkill =
  | "answer"
  | "investigate"
  | "explain-record"
  | "explain-code"
  | "build-list"
  | "build-page";

/** The index's tabs: yours, those shared with the company, and Adea's own work (routines and events). */
export type ThreadScope = "mine" | "shared" | "routines";
export const SCOPES: ThreadScope[] = ["mine", "shared", "routines"];

/** Mine and Team are the questions people asked; Adea's own work (the study, the nightly check…) has its own tab. */
export const listInput = (scope: ThreadScope, limit = 50) =>
  scope === "routines"
    ? { scope: "all" as const, startedBy: ["routine" as const, "event" as const], limit }
    : { scope, startedBy: ["person" as const], limit };

export const threadHref = (threadId: string) => `/threads/${threadId}`;
export const fileHref = (fileId: string) => `/api/v1/thread-files/${fileId}`;

/** A measure's name inside a sentence: "Conversion" reads "conversion", "MRR" and "B prices" stay as they are. */
const inSentence = (name: string) =>
  /^\p{Lu}\p{Ll}/u.test(name) ? name[0]?.toLowerCase() + name.slice(1) : name;

/** What an investigation Watch started looked into (`event.noticed`, kept by `watch_investigate`). */
function noticedOf(event: Thread["event"]): { measure: string; segment: string | null; move: string } | null {
  const n = event?.noticed as Record<string, unknown> | undefined;
  if (!n || typeof n.measure !== "string" || !n.measure.trim() || typeof n.move !== "string") return null;
  return {
    measure: n.measure.trim(),
    segment: typeof n.segment === "string" && n.segment.trim() ? n.segment.trim() : null,
    move: n.move,
  };
}

/**
 * The routine's opening prompt is internal; people see one line of what Adea noticed and looked into ("Adea noticed
 * conversion rose and looked into it"), with the routine behind it as a quiet detail.
 */
export function threadOpening(
  thread: Pick<Thread, "startedBy" | "routineName" | "title" | "event"> | undefined,
  t: AdeaT,
): string | null {
  if (!thread || thread.startedBy === "person") return null;
  const from = thread.routineName?.trim();
  const noticed = noticedOf(thread.event);
  let line: string;
  if (noticed) {
    const measure = inSentence(noticed.measure);
    line =
      noticed.move === "goal"
        ? t("threads.page.opening.goal", { measure })
        : noticed.move === "data"
          ? t("threads.page.opening.data", { measure })
          : noticed.segment
            ? t(`threads.page.opening.${noticed.move === "down" ? "fellIn" : "roseIn"}`, {
                measure,
                segment: noticed.segment,
              })
            : t(`threads.page.opening.${noticed.move === "down" ? "fell" : "rose"}`, { measure });
  } else if (thread.event?.kind === "watch_candidate" && thread.title.trim()) {
    // investigations started before Watch kept what it noticed: the title is the measure, maybe with its segment
    const [measure = "", segment] = thread.title.trim().split(": ");
    line = segment
      ? t("threads.page.opening.changeIn", { measure: inSentence(measure), segment })
      : t("threads.page.opening.change", { measure: inSentence(measure) });
  } else if (thread.startedBy === "event") {
    line = t("threads.page.opening.event");
  } else {
    line = t("threads.page.opening.routine");
  }
  return from ? t("threads.page.opening.from", { line, routine: from }) : line;
}

/** The run fold's step for words written outside any tool step. */
const ANSWER = "_answer";

const KINDS: Record<string, ThreadStepKind> = {
  sql: "query",
  schema: "schema",
  find_record: "record",
  code_search: "codeSearch",
  code_read: "code",
  code_history: "history",
  python: "python",
  understanding_read: "understanding",
  understanding_edit: "understanding",
  library_find: "recipe",
  recipe_run: "recipe",
  recipe_save: "recipe",
  keep: "recipe",
  spawn: "spawn",
  ask: "ask",
};

const stepOf = (s: ToolStep): ThreadStep => ({
  key: s.key,
  kind: KINDS[s.tool] ?? "other",
  label: s.label,
  ...(s.table ? { name: s.table } : {}),
  state: s.status === "started" ? "running" : s.status === "failed" ? "failed" : "done",
});

/**
 * A question just sent, before the conversation has it back: the question, and Adea starting on it under it. Keyed
 * by the numbers the stored messages will get (`seq`: the question's), so the stored ones take their place without
 * the view drawing anything twice.
 */
export function pendingTurn(seq: number, text: string, attachments: Attachment[]): ViewMessage[] {
  return [
    {
      key: String(seq),
      role: "person",
      text,
      attachments: attachments.map((a) => ({ key: a.fileId, src: fileHref(a.fileId), alt: a.name })),
    },
    { key: String(seq + 1), role: "adea", state: "thinking", steps: [] },
  ];
}

/**
 * The stored messages as the thread view draws them. The answer still being worked on takes its steps and words from
 * the live run (`live`), until the run ends and the stored message has them.
 */
export function viewMessages(
  messages: ThreadMessage[],
  live: RunState,
  /** Who wrote a question, when it wasn't the reader (a colleague in a shared thread). */
  authorOf: (userId: string) => string | undefined,
  thread?: Pick<Thread, "startedBy">,
): ViewMessage[] {
  const lastAdea = messages.findLast((m) => m.role === "adea");
  const shown = messages.filter(
    (m) => !(thread && thread.startedBy !== "person" && m.seq === 1 && m.role === "person"),
  );
  return shown.map((m): ViewMessage => {
    const key = String(m.seq);
    if (m.role === "person")
      return {
        key,
        role: "person",
        text: m.content,
        attachments: m.attachments.map((a) => ({ key: a.fileId, src: fileHref(a.fileId), alt: a.name })),
        author: m.userId ? authorOf(m.userId) : undefined,
      };
    const working = m.status === "working";
    const following = working && m === lastAdea && live.runId === m.runId;
    const steps = following
      ? live.steps
          .filter((s) => s.key !== ANSWER)
          .map(
            (s): ThreadStep => ({
              key: s.key,
              kind: "other",
              label: s.label,
              state: s.state === "running" ? "running" : s.state === "failed" ? "failed" : "done",
            }),
          )
      : m.steps.map(stepOf);
    const text = following ? (live.steps.find((s) => s.key === ANSWER)?.text ?? "") : m.content;
    return {
      key,
      role: "adea",
      state: working
        ? steps.length
          ? "working"
          : "thinking"
        : m.ask
          ? "asking"
          : m.status === "done"
            ? "done"
            : m.status,
      text,
      blocks: m.blocks,
      steps,
      ...(m.ask
        ? {
            ask: {
              question: m.ask.question,
              options: m.ask.choices.map((c) => ({ key: c, label: c })),
              picked:
                m === lastAdea
                  ? null
                  : (messages.find((n) => n.seq === m.seq + 1 && n.role === "person")?.content ?? null),
            },
          }
        : {}),
    };
  });
}

/** A number as its unit reads: a share as a percentage, money in its currency. */
function glanceNumber(v: number, unit: string | undefined, currency: string | undefined, format: Formatter) {
  if (unit === "share") return format.percent(v, { digits: Math.abs(v) < 0.1 ? 1 : 0 });
  if (unit === "money" && currency) return format.currency(v, currency, { maxDigits: 0 });
  return format.number(v);
}

/** A thread's latest answer at a glance, for its row in the index: its number, its line, its bars or its size. */
export function glanceOf(
  block: Block | null | undefined,
  format: Formatter,
  rows: (count: number) => string,
): GlanceData | null {
  if (!block) return null;
  switch (block.shape) {
    case "number": {
      const rel = block.change?.rel;
      return {
        kind: "number",
        value:
          block.value === null ? "–" : glanceNumber(block.value, block.unit, block.format?.currency, format),
        ...(rel !== undefined && rel !== null
          ? {
              delta: format.percentChange(rel, { digits: 1 }),
              tone: rel > 0 ? "up" : rel < 0 ? "down" : "flat",
            }
          : {}),
      };
    }
    case "series": {
      const points = (block.series[0]?.points ?? []).flatMap((p) =>
        p.v === null ? [] : [{ date: new Date(p.t), v: p.v, lo: p.low ?? p.v, hi: p.high ?? p.v }],
      );
      const last = points.at(-1);
      return points.length > 1
        ? { kind: "series", series: points, value: last ? format.number(last.v) : "–" }
        : null;
    }
    case "breakdown": {
      const max = Math.max(1e-9, ...block.rows.map((r) => Math.abs(r.value ?? 0)));
      return {
        kind: "breakdown",
        bars: block.rows.slice(0, 4).map((r) => ({ label: r.label, share: Math.abs(r.value ?? 0) / max })),
      };
    }
    case "table":
    case "entities":
      return { kind: "entities", count: format.integer(block.rowCount), noun: rows(block.rowCount) };
    default:
      return null;
  }
}

/** Adea's own runs that read best as what they do, by skill (the study, the nightly check…), with the day they ran. */
const RUN_TITLES: Record<string, "nightly" | "discovery" | "shipped"> = {
  "watch-review": "nightly",
  discovery: "discovery",
  "whats-shipped": "shipped",
};
const STUDY = /^study($|-)/;

/**
 * What a conversation is called in a list or its page. A person's question is its own words. Adea's own work says
 * what it does ("Learning how Steelheart works", "Nightly check, 9 Oct"), also for runs saved under the routine's
 * name alone ("Study a new source"); a title naming something of its own (a code change, a recipe) is kept.
 */
export function threadTitle(
  th: Pick<Thread, "title" | "startedBy" | "skill" | "routineName" | "createdAt">,
  t: AdeaT,
  format: Pick<Formatter, "date">,
  company?: string | null,
): string {
  if (th.startedBy === "person") return th.title || t("threads.index.untitled");
  const day = format.date(th.createdAt, "dayMonth");
  if (STUDY.test(th.skill))
    return company ? t("threads.title.study", { company }) : t("threads.title.studyPlain");
  const keyed = RUN_TITLES[th.skill];
  if (keyed) return t(`threads.title.${keyed}`, { day });
  const named = th.title && th.title !== th.routineName ? th.title : null;
  if (named) return named;
  const base = th.routineName || th.title;
  return base ? t("threads.title.dated", { name: base, day }) : t("threads.index.untitled");
}
